import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import Loader from "../../components/spinner/Spinner";
import {
  fetchAllSubscriptions,
  cancelSubscription,
  pauseSubscription,
  resumeSubscription,
} from "../../services/subscription";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import "./subscriptions.scss";
import { formatDate } from "../../utils/date";

const SubscriptionList = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isSidebar, setIsSidebar] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadSubscriptions();
  }, [statusFilter, pagination.page]);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (statusFilter && statusFilter !== "all") {
        params.status = statusFilter;
      }

      const response = await fetchAllSubscriptions(params);
      if (response.data.status) {
        const rows = response.data.subscriptions || [];
        const sortedRows = [...rows].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
        setSubscriptions(sortedRows);
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          totalPages: response.data.pagination?.totalPages || 0,
        }));
      }
    } catch (error) {
      console.error("Failed to load subscriptions:", error);
      alert("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (sub) => {
    if (!window.confirm(`Cancel subscription for ${sub.kid_name}?`)) return;

    setActionLoading(sub.id);
    try {
      await cancelSubscription(sub.id, false);
      alert("Subscription cancelled");
      loadSubscriptions();
    } catch (error) {
      alert("Failed to cancel subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async (sub) => {
    setActionLoading(sub.id);
    try {
      await pauseSubscription(sub.id);
      alert("Subscription paused");
      loadSubscriptions();
    } catch (error) {
      alert("Failed to pause subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (sub) => {
    setActionLoading(sub.id);
    try {
      await resumeSubscription(sub.id);
      alert("Subscription resumed");
      loadSubscriptions();
    } catch (error) {
      alert("Failed to resume subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: "Active", className: "status-active" },
      authenticated: { label: "Pending", className: "status-pending" },
      created: { label: "Created", className: "status-created" },
      paused: { label: "Paused", className: "status-paused" },
      cancelled: { label: "Cancelled", className: "status-cancelled" },
      completed: { label: "Completed", className: "status-completed" },
      halted: { label: "Halted", className: "status-halted" },
      pending: { label: "Pending", className: "status-pending" },
    };
    const c = config[status] || config.pending;
    return <span className={`statusBadge ${c.className}`}>{c.label}</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Calculate next due date based on current_end or charge_at
  const getNextDueDate = (sub) => {
    // Priority: charge_at > current_end > calculate from created_at
    if (sub.charge_at) {
      return formatDate(sub.charge_at);
    }
    if (sub.current_end) {
      return formatDate(sub.current_end);
    }
    // If no charge_at, calculate next month from created_at
    if (
      sub.created_at &&
      (sub.status === "active" ||
        sub.status === "created" ||
        sub.status === "authenticated")
    ) {
      const createdDate = new Date(sub.created_at);
      const nextDue = new Date(createdDate);
      nextDue.setMonth(nextDue.getMonth() + (sub.paid_count || 0) + 1);
      return formatDate(nextDue);
    }
    return "-";
  };

  return (
    <div className="list">
      <Sidebar isSidebar={isSidebar} />
      <div className="listContainer">
        <Navbar isSidebar={isSidebar} setIsSidebar={setIsSidebar} />

        <div className="subscriptionListContainer">
          <div className="pageHeader">
            <div className="headerLeft">
              <h1>Subscriptions</h1>
              <p>Manage recurring sponsorship subscriptions</p>
            </div>
            <div className="headerRight">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className="statusFilter"
              >
                <option value="all">All Active</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
                <option value="halted">Halted</option>
              </select>
            </div>
          </div>

          {loading ? (
            <Loader />
          ) : subscriptions.length === 0 ? (
            <div className="emptyState">
              <h3>No Subscriptions Found</h3>
              <p>There are no subscriptions matching your filter.</p>
            </div>
          ) : (
            <>
              <div className="subscriptionsTableShell">
                <div className="subscriptionsTable">
                  <table>
                    <thead>
                      <tr>
                        <th>Id</th>
                        <th>Kid</th>
                        <th>Sponsor</th>
                        <th>Amount</th>
                        <th>Progress</th>
                        <th>Status</th>
                        <th>Next Due Date</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => (
                        <tr key={sub.id}>
                          <td>#{sub.id}</td>
                          <td>
                            <div className="kidCell">
                              <img
                                src={sub.kid_avatar || "/img/logo.png"}
                                alt={sub.kid_name}
                                className="kidAvatar"
                              />
                              <span>{sub.kid_name || "N/A"}</span>
                            </div>
                          </td>
                          <td>
                            <div className="sponsorCell">
                              <span className="name">
                                {sub.is_anonymous
                                  ? "Anonymous"
                                  : sub.mentor_name || sub.customer_name || "-"}
                              </span>
                              <span className="email">
                                {sub.customer_email}
                              </span>
                            </div>
                          </td>
                          <td>{formatCurrency(sub.amount)}/mo</td>
                          <td>
                            <div className="progressCell">
                              <span>
                                {sub.paid_count || 0}/{sub.total_count}
                              </span>
                              <div className="miniProgress">
                                <div
                                  className="miniProgressFill"
                                  style={{
                                    width: `${sub.total_count ? ((sub.paid_count || 0) / sub.total_count) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                          <td>{getStatusBadge(sub.status)}</td>
                          <td>
                            <span
                              className={
                                sub.status === "active" ? "nextDueActive" : ""
                              }
                            >
                              {getNextDueDate(sub)}
                            </span>
                          </td>
                          <td>{formatDate(sub.created_at)}</td>
                          <td>
                            <div className="actionCell">
                              <button
                                onClick={() =>
                                  navigate(`/subscriptions/${sub.id}`)
                                }
                                className="actionBtn viewBtn"
                                title="View details"
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </button>
                              {sub.status === "active" && (
                                <>
                                  <button
                                    onClick={() => handlePause(sub)}
                                    disabled={actionLoading === sub.id}
                                    className="actionBtn pauseBtn"
                                    title="Pause subscription"
                                  >
                                    <PauseCircleOutlinedIcon fontSize="small" />
                                  </button>
                                  <button
                                    onClick={() => handleCancel(sub)}
                                    disabled={actionLoading === sub.id}
                                    className="actionBtn cancelBtn"
                                    title="Cancel subscription"
                                  >
                                    <CancelOutlinedIcon fontSize="small" />
                                  </button>
                                </>
                              )}
                              {sub.status === "paused" && (
                                <button
                                  onClick={() => handleResume(sub)}
                                  disabled={actionLoading === sub.id}
                                  className="actionBtn resumeBtn"
                                  title="Resume subscription"
                                >
                                  <PlayCircleOutlinedIcon fontSize="small" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {pagination.total > 0 && (
                <div className="pagination">
                  <button
                    type="button"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  <span>
                    Showing {(pagination.page - 1) * pagination.limit + 1}-
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total,
                    )}{" "}
                    of {pagination.total}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionList;
